import { sleep, calculateStats, getValuesForPath } from "./utils";

export const getGyroscope = () => {
  if (!("Gyroscope" in window)) return undefined; // Gyroscope isn't supported by some browsers like Firefox and Safari.

  const gyroscope = new window.Gyroscope({ frequency: 60 });
  return gyroscope;
};

export const getGyroscopeRead = async () => {
  const gyroscope = getGyroscope();
  if (!gyroscope) return undefined; // API not present on this browser/device.

  const readings = [];
  let lastReading;
  let denied = false;

  const listener = () => {
    const { x, y, z } = gyroscope;
    const reading = { x, y, z };
    readings.push(reading);
    lastReading = reading;
  };
  // The Generic Sensor API reports permission/policy denial via an "error"
  // event rather than always throwing synchronously from start().
  let resolveDenial;
  const denialPromise = new Promise((resolve) => { resolveDenial = resolve; });
  const errorListener = () => {
    denied = true;
    resolveDenial();
  };
  gyroscope.addEventListener("reading", listener);
  gyroscope.addEventListener("error", errorListener);
  try {
    gyroscope.start();
  } catch (_e) {
    denied = true;
  }

  // Collect readings over 2 seconds, but stop early if denied so we don't
  // wait out the full window for a sensor we already know isn't readable.
  if (!denied) await Promise.race([sleep(2000), denialPromise]);

  gyroscope.removeEventListener("reading", listener);
  gyroscope.removeEventListener("error", errorListener);
  gyroscope.stop();

  if (denied) return undefined; // Permission or Permissions-Policy denied.
  if (readings.length === 0) return []; // Sensor exists, no samples taken.

  const stats = {
    x: calculateStats(getValuesForPath(readings, 'x')),
    y: calculateStats(getValuesForPath(readings, 'y')),
    z: calculateStats(getValuesForPath(readings, 'z')),
  };

  return {
    lastReading,
    stats,
    sampleCount: readings.length,
  };
};

// Same split as getGyroscopeRead. Caveat: the window orientation
// event has no denial callback, so "permission denied" and "granted, no
// motion" both land in the [] case; only true API absence maps to undefined.
export const getDeviceOrientationRead = async () => {
  if (!("ondeviceorientation" in window)) return undefined; // Not supported.

  const readings = [];
  let lastReading;

  const listener = (event) => {
    const reading = {
      ...event, // Include the event in case it contains extra information.
      alpha: event?.alpha,
      beta: event?.beta,
      gamma: event?.gamma,
    };
    readings.push(reading);
    lastReading = reading;
  };
  window.addEventListener("deviceorientation", listener);

  // Collect readings over 2 seconds.
  await sleep(2000);

  window.removeEventListener("deviceorientation", listener);

  if (readings.length === 0) return []; // Supported, no samples taken.

  const stats = {
    alpha: calculateStats(getValuesForPath(readings, 'alpha')),
    beta: calculateStats(getValuesForPath(readings, 'beta')),
    gamma: calculateStats(getValuesForPath(readings, 'gamma')),
  };

  return {
    lastReading,
    stats,
    sampleCount: readings.length,
  };
};
